import React, { useState, useEffect, useRef } from 'react';
import { Radio, Checkbox, message, Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import SuccessModal from '../../../components/SuccessModal';
import ErrorModal from '../../../components/ErrorModal';
import { getSurveyPaper, getSurveyProgress, submitSurvey } from '../../../api';
import { getApiErrorMessage } from '../../../utils/apiErrorUtils';
import { useEnterprise } from '../../../contexts/EnterpriseContext';
import { questionnaireNoticeData } from '../../../mockData/questionnaireNotice';
import './index.css';
import './index.mobile.css';

const FFQ_NOTICE_SEEN_KEY = 'ffq_notice_seen';

const MODULE_STATUS_LABEL = {
  done: 'ff.questionnaire.status.done',
  doing: 'ff.questionnaire.status.doing',
  undone: 'ff.questionnaire.status.undone',
};

export default function Questionnaire() {
  const { t, i18n } = useTranslation();
  const { enterprise } = useEnterprise() || {};

  const [modules, setModules] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [currentModule, setCurrentModule] = useState(null);
  const [currentQ, setCurrentQ] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [incompleteFields, setIncompleteFields] = useState([]);
  const [showNoticeModal, setShowNoticeModal] = useState(false);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!sessionStorage.getItem(FFQ_NOTICE_SEEN_KEY)) {
      setShowNoticeModal(true);
      sessionStorage.setItem(FFQ_NOTICE_SEEN_KEY, '1');
    }
  }, []);

  useEffect(() => {
    const local = i18n.language;
    const isInitial = !initializedRef.current;

    if (isInitial) {
      Promise.all([getSurveyPaper(local), getSurveyProgress()])
        .then(([paperRes, progressRes]) => {
          const mods = paperRes.result?.modules || [];
          setModules(mods);
          const allQuestions = mods.flatMap((m) =>
            (m.questions || []).map((q) => ({ ...q, moduleName: m.name, moduleId: m.id }))
          );
          setQuestions(allQuestions);
          if (mods.length) setCurrentModule(mods[0].id);
          if (allQuestions.length) setCurrentQ(allQuestions[0].id);

          const savedAnswers = {};
          (progressRes.result?.answers || []).forEach((a) => {
            savedAnswers[a.questionId] = {
              optionId: a.optionId,
              optionIds: a.optionIds || [],
              textAnswer: a.textAnswer,
            };
          });
          setAnswers(savedAnswers);
          setSubmitted(progressRes.result?.surveyProgress === 2);
          initializedRef.current = true;
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      getSurveyPaper(local)
        .then((paperRes) => {
          const mods = paperRes.result?.modules || [];
          setModules(mods);
          setQuestions(
            mods.flatMap((m) =>
              (m.questions || []).map((q) => ({ ...q, moduleName: m.name, moduleId: m.id }))
            )
          );
        })
        .catch(() => {});
    }
  }, [i18n.language]);

  const isVisible = (q) => {
    const deps = Array.isArray(q.visibleWhenOptionIds) ? q.visibleWhenOptionIds : [];
    if (!deps.length) return true;
    return deps.some((optId) =>
      Object.values(answers).some(
        (a) => a.optionId === optId || (Array.isArray(a.optionIds) && a.optionIds.includes(optId))
      )
    );
  };

  const visibleQuestions = questions.filter(isVisible);
  const currentModuleObj = modules.find((m) => m.id === currentModule);
  const moduleQuestions = questions.filter((q) => q.moduleId === currentModule);
  const visibleModuleQuestions = moduleQuestions.filter(isVisible);
  const currentQuestion = visibleQuestions.find((q) => q.id === currentQ);
  const globalIndex = visibleQuestions.findIndex((q) => q.id === currentQ);
  const moduleQIndex = visibleModuleQuestions.findIndex((q) => q.id === currentQ);
  const isAnswered = (q) => {
    const a = answers[q.id];
    if (!a) return false;
    if (q.questionType === 3 || q.questionType === 9)
      return Array.isArray(a.optionIds) && a.optionIds.length > 0;
    return !!(a.optionId || a.textAnswer);
  };
  const answeredCount = visibleQuestions.filter(isAnswered).length;

  const moduleStatuses = modules.map((m) => {
    const mqs = visibleQuestions.filter((q) => q.moduleId === m.id);
    const answeredAll = mqs.length > 0 && mqs.every((q) => isAnswered(q));
    const answeredSome = mqs.some((q) => isAnswered(q));
    return {
      ...m,
      key: m.id,
      label: m.name,
      status: answeredAll ? 'done' : answeredSome ? 'doing' : 'undone',
    };
  });

  const navigateTo = (q) => {
    if (!q) return;
    setCurrentQ(q.id);
    if (q.moduleId !== currentModule) setCurrentModule(q.moduleId);
  };

  // 当前题目变为不可见时，跳到前一道可见题
  useEffect(() => {
    if (!currentQ || !questions.length) return;
    const q = questions.find((item) => item.id === currentQ);
    if (q && !isVisible(q)) {
      const idx = visibleQuestions.findIndex((vq) => vq.id === currentQ);
      const target = visibleQuestions[idx - 1] ?? visibleQuestions[0];
      if (target) navigateTo(target);
    }
  }, [answers]); // eslint-disable-line react-hooks/exhaustive-deps

  const setAnswerWithClear = (updater) => {
    setAnswers((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      // 清除因本次选择而变为隐藏的题的答案
      const cleaned = { ...next };
      questions.forEach((q) => {
        if (
          Array.isArray(q.visibleWhenOptionIds) &&
          q.visibleWhenOptionIds.length &&
          cleaned[q.id]
        ) {
          const visible = q.visibleWhenOptionIds.some((optId) =>
            Object.values(cleaned).some(
              (a) =>
                a.optionId === optId || (Array.isArray(a.optionIds) && a.optionIds.includes(optId))
            )
          );
          if (!visible) delete cleaned[q.id];
        }
      });
      return cleaned;
    });
  };

  const buildAnswerList = () =>
    Object.entries(answers).map(([questionId, val]) => ({
      questionId,
      optionId: val.optionId || null,
      optionIds: val.optionIds?.length ? val.optionIds : null,
      textAnswer: val.textAnswer || null,
    }));

  const handleSaveDraft = async () => {
    if (!enterprise?.id) return;
    message.config({ top: 100 });
    try {
      await submitSurvey(enterprise.id, 1, buildAnswerList());
      message.success(t('ff.questionnaire.saveDraftSuccess'));
    } catch {
      message.error(t('ff.questionnaire.saveDraftError'));
    }
  };

  const handleSubmit = async () => {
    const incomplete = [
      ...new Set(
        visibleQuestions
          .filter((q) => q.required === true && !isAnswered(q))
          .map((q) => q.moduleName)
      ),
    ];
    if (incomplete.length > 0) {
      setIncompleteFields(incomplete);
      setShowIncompleteModal(true);
      return;
    }
    if (!enterprise?.id) return;
    try {
      await submitSurvey(enterprise.id, 2, buildAnswerList());
      setSubmitted(true);
      setShowSuccessModal(true);
    } catch (err) {
      setIncompleteFields([getApiErrorMessage(err.message, t, 'ff.questionnaire.submitError')]);
      setShowIncompleteModal(true);
    }
  };

  if (loading) return <div className="ffq-page" />;

  return (
    <div className="ffq-page">
      <h1 className="ffq-title">{t('ff.questionnaire.title')}</h1>
      <p className="ffq-subtitle">
        {t('ff.questionnaire.subtitle', { count: modules.length })}
        <button className="ffq-notice-entry" onClick={() => setShowNoticeModal(true)}>
          {t('ff.questionnaire.subtitle.link')}
        </button>
        {t('ff.questionnaire.subtitle.post')}
      </p>

      <div className="ffq-card">
        <div className="ffq-card-content">
          <div className="ffq-card-content-left">
            <div className="ffq-progress-header">
              <div className="ffq-progress-row">
                <span className="ffq-section-label">{t('ff.questionnaire.progressLabel')}</span>
                <div className="ffq-modules-wrap">
                  {moduleStatuses.map((mod, i) => (
                    <React.Fragment key={mod.key}>
                      <div className="ffq-module-col">
                        <button
                          className={`ffq-module-pill ffq-module-pill--${mod.status} ${currentModule === mod.key ? 'ffq-module-pill--selected' : ''}`}
                          onClick={() => {
                            setCurrentModule(mod.key);
                            const first = questions.find((q) => q.moduleId === mod.key);
                            if (first) setCurrentQ(first.id);
                          }}
                          ref={(btn) => {
                            if (!btn) return;
                            const labelEl = btn.querySelector('.ffq-module-pill__label');
                            if (labelEl && labelEl.scrollWidth > labelEl.clientWidth) {
                              btn.setAttribute('data-label', mod.label);
                            } else {
                              btn.removeAttribute('data-label');
                            }
                          }}
                        >
                          <span className="ffq-module-pill__label">{mod.label}</span>
                        </button>
                        <span
                          className={`ffq-module-status-label ffq-module-status-label--${mod.status}`}
                        >
                          {t(MODULE_STATUS_LABEL[mod.status])}
                        </span>
                      </div>
                      {i < moduleStatuses.length - 1 && (
                        <div
                          className={`ffq-module-connector ${mod.status === 'done' ? 'ffq-module-connector--done' : ''}`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="ffq-progress-row ffq-progress-row--card">
                <span className="ffq-section-label">{t('ff.questionnaire.answerCardLabel')}</span>
                <div className="ffq-answer-grid">
                  {visibleModuleQuestions.map((q, idx) => (
                    <button
                      key={q.id}
                      className={`ffq-answer-cell ${q.id === currentQ ? 'ffq-answer-cell--current' : isAnswered(q) ? 'ffq-answer-cell--done' : ''}`}
                      onClick={() => setCurrentQ(q.id)}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {currentQuestion && (
              <div className="ffq-body">
                <div className="ffq-question-area">
                  {currentQuestion.type ? (
                    <div className="ffq-module-tag">
                      <span className="ffq-module-tag-name">{currentQuestion.type}</span>
                    </div>
                  ) : null}

                  <div className="ffq-question-content">
                    <div className="ffq-question-text">
                      <span className="ffq-question-num">{moduleQIndex + 1}.</span>
                      <span className="ffq-question-label">{currentQuestion.title}</span>
                    </div>

                    {currentQuestion.note && (
                      <div className="ffq-question-note">
                        <ReactMarkdown>{currentQuestion.note}</ReactMarkdown>
                      </div>
                    )}

                    {currentQuestion.reminder && (
                      <div className="ffq-question-note">
                        <ReactMarkdown>{currentQuestion.reminder}</ReactMarkdown>
                      </div>
                    )}

                    {currentQuestion.questionType === 1 ? (
                      <div className="ffq-options">
                        {(currentQuestion.options || []).map((opt) => {
                          const selected = answers[currentQuestion.id]?.optionId === opt.id;
                          return (
                            <Radio
                              key={opt.id}
                              className="ffq-option-radio"
                              checked={selected}
                              disabled={submitted}
                              onChange={() =>
                                setAnswerWithClear((prev) => ({
                                  ...prev,
                                  [currentQuestion.id]: { optionId: opt.id },
                                }))
                              }
                            >
                              <span className="ffq-option-text">
                                {opt.letter}. {opt.content}
                              </span>
                            </Radio>
                          );
                        })}
                      </div>
                    ) : currentQuestion.questionType === 3 || currentQuestion.questionType === 9 ? (
                      <div className="ffq-options">
                        {(currentQuestion.options || []).map((opt) => {
                          const selectedIds = answers[currentQuestion.id]?.optionIds || [];
                          const checked = selectedIds.includes(opt.id);
                          const maxReached =
                            currentQuestion.questionType === 3 && selectedIds.length >= 2;
                          return (
                            <Checkbox
                              key={opt.id}
                              className="ffq-option-radio"
                              checked={checked}
                              disabled={submitted || (!checked && maxReached)}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...selectedIds, opt.id]
                                  : selectedIds.filter((id) => id !== opt.id);
                                setAnswers((prev) => {
                                  if (!next.length) {
                                    const cleaned = { ...prev };
                                    delete cleaned[currentQuestion.id];
                                    return cleaned;
                                  }
                                  return {
                                    ...prev,
                                    [currentQuestion.id]: { optionIds: next },
                                  };
                                });
                              }}
                            >
                              <span className="ffq-option-text">
                                {opt.letter}. {opt.content}
                              </span>
                            </Checkbox>
                          );
                        })}
                      </div>
                    ) : (
                      <textarea
                        className="ffq-textarea"
                        placeholder={t('ff.questionnaire.textPlaceholder')}
                        disabled={submitted}
                        value={answers[currentQuestion.id]?.textAnswer || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setAnswers((prev) => {
                            if (val) return { ...prev, [currentQuestion.id]: { textAnswer: val } };
                            const next = { ...prev };
                            delete next[currentQuestion.id];
                            return next;
                          });
                        }}
                      />
                    )}
                  </div>

                  <div className="ffq-nav-btns">
                    <button
                      className="ffq-nav-btn"
                      onClick={() => navigateTo(visibleQuestions[globalIndex - 1])}
                      disabled={globalIndex <= 0}
                    >
                      {t('ff.questionnaire.prevQuestion')}
                    </button>
                    <button
                      className="ffq-nav-btn ffq-nav-btn--next"
                      onClick={() => navigateTo(visibleQuestions[globalIndex + 1])}
                      disabled={globalIndex >= visibleQuestions.length - 1}
                    >
                      {t('ff.questionnaire.nextQuestion')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {currentModuleObj?.content ? (
            <div className="ffq-card-content-right">
              <div className="ffq-hint-panel">
                <ReactMarkdown>{currentModuleObj.content}</ReactMarkdown>
              </div>
            </div>
          ) : null}
        </div>

        <div className="ffq-bottom-bar">
          <span className="ffq-progress-text">
            {t('ff.questionnaire.answeredCount')}
            <span className="ffq-progress-answered">{answeredCount}</span>{' '}
            <span className="ffq-progress-total">/ {visibleQuestions.length}</span>
          </span>
          <div className="ffq-bottom-actions">
            <button className="ffq-btn-submit" onClick={handleSubmit} disabled={submitted}>
              {t('ff.questionnaire.submit')}
            </button>
            <button className="ffq-btn-draft" onClick={handleSaveDraft} disabled={submitted}>
              {t('ff.questionnaire.saveDraft')}
            </button>
          </div>
        </div>
      </div>

      <SuccessModal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={t('ff.questionnaire.success.title')}
        desc={t('ff.questionnaire.success.desc')}
        btnText={t('ff.questionnaire.success.btn')}
      />

      <ErrorModal
        open={showIncompleteModal}
        onClose={() => setShowIncompleteModal(false)}
        title={t('ff.questionnaire.incomplete.title')}
        desc1={t('ff.questionnaire.incomplete.desc1')}
        desc2={t('ff.questionnaire.incomplete.desc2')}
        fieldTitle={t('ff.questionnaire.incomplete.pending')}
        fieldContent={incompleteFields.join('、')}
        btnText={t('ff.questionnaire.incomplete.btn')}
      />

      <NoticeModal
        open={showNoticeModal}
        onClose={() => setShowNoticeModal(false)}
        title={t('ff.questionnaire.notice.title')}
        btnText={t('ff.questionnaire.notice.btn')}
        data={questionnaireNoticeData[i18n.language] ?? questionnaireNoticeData.zh}
      />
    </div>
  );
}

function NoticeModal({ open, onClose, title, btnText, data }) {
  return (
    <Modal
      open={open}
      footer={null}
      onCancel={onClose}
      centered
      width={900}
      className="ffq-info-modal"
      classNames={{ content: 'ffq-info-modal-content' }}
      modalRender={(modal) => (
        <div className="ffq-info-modal-wrap">
          {modal}
          {btnText && (
            <div className="ffq-info-modal-footer">
              <button className="ffq-info-modal-btn" onClick={onClose}>
                {btnText}
              </button>
            </div>
          )}
        </div>
      )}
    >
      <div className="ffq-info-modal-header">
        <h3 className="ffq-info-modal-title">{title}</h3>
        <FfqCloseBtn onClick={onClose} />
      </div>
      <div
        className="ffq-info-modal-background"
        style={{
          backgroundImage: `url("${process.env.PUBLIC_URL}/image/Group_67.png")`,
        }}
      />
      <div className="ffq-info-modal-scroll">
        <div className="ffq-info-modal-body">
          {data && (
            <div className="ffq-notice-markdown">
              <ReactMarkdown>{data}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function FfqCloseBtn({ onClick, className }) {
  return (
    <button
      type="button"
      className={`ffq-close-btn${className ? ` ${className}` : ''}`}
      aria-label="close"
      onClick={onClick}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path
          d="M1 1L13 13M13 1L1 13"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}
